import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DraftProvider, useDraft } from '../contexts/DraftContext';
import DraftBoard from '../components/DraftBoard';
import PickTimer from '../components/PickTimer';

// Mirrors DraftControls.jsx's / DraftBoard.jsx's calculateTeamIndex / DraftService.calculateTeamIndex.
function calculateTeamIndex(draftType, teamCount, round, pickNumber) {
  if (draftType?.toLowerCase() === 'snake' && round % 2 === 0) {
    return teamCount - pickNumber;
  }
  return pickNumber - 1;
}

/**
 * Read-only, big-screen-oriented view of a draft room -- reached from the Commissioner Mode
 * lobby's "Open TV Display" link (see DraftLobbyView.jsx). Shows only the board itself
 * (round/pick indicator, on-clock team, pick timer, full picks grid) -- no available-players
 * list, no roster panel, no pick controls; this screen is never used to make a pick.
 *
 * Wraps DraftProvider the same way DraftView.jsx does, so access control (canViewDraft) and live
 * polling fall out for free -- no new backend route, no new auth model, no new polling mechanism.
 */
function DraftTvView() {
  const { draftId } = useParams();
  return (
    <DraftProvider draftId={draftId}>
      <DraftTvViewInner />
    </DraftProvider>
  );
}

function DraftTvViewInner() {
  const { draft, picks, draftTeams, loading, isOwner, skipExpiredPick, extendTimer } = useDraft();
  // null sentinel (not 0) so the very first render establishes a baseline instead of flashing
  // "just picked" for a pick that happened before this screen was ever opened.
  const lastPickCountRef = useRef(null);
  const [justPicked, setJustPicked] = useState(null);

  useEffect(() => {
    if (lastPickCountRef.current !== null && picks.length > lastPickCountRef.current) {
      const newest = [...picks].sort((a, b) => (a.overallPick ?? 0) - (b.overallPick ?? 0)).at(-1);
      setJustPicked(newest);
      const timeout = setTimeout(() => setJustPicked(null), 6000);
      lastPickCountRef.current = picks.length;
      return () => clearTimeout(timeout);
    }
    lastPickCountRef.current = picks.length;
  }, [picks]);

  // Best-effort screen wake lock -- keeps a shared/TV display from dimming or sleeping mid-draft.
  // Feature-detected and silently no-op on unsupported browsers (notably Safari < 16.4): a TV
  // going to sleep is an annoyance, not a correctness bug, so this never blocks rendering.
  useEffect(() => {
    if (!('wakeLock' in navigator)) return;
    let sentinel;
    navigator.wakeLock.request('screen').then(s => { sentinel = s; }).catch(() => {});
    return () => { sentinel?.release?.().catch(() => {}); };
  }, []);

  if (loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading draft room...</p>
      </div>
    );
  }

  if (!draft) {
    return <div className='error-banner'>Draft not found.</div>;
  }

  const { teamCount, draftType, currentRound, currentPick, rounds, status } = draft;
  const isCompleted = status === 'completed';
  const currentTeamIndex = !isCompleted
    ? calculateTeamIndex(draftType, teamCount, currentRound, currentPick)
    : -1;
  const currentTeam = currentTeamIndex >= 0
    ? draftTeams.find(t => t.slotIndex === currentTeamIndex)
    : null;
  // A team's own name is often left at its generic "Team N" default -- surface the guest's
  // display name whenever known, same as DraftBoard's column headers (see its comment on why a
  // linked Opponent's name isn't duplicated here too).
  const currentOccupantName = currentTeam?.guestDisplayName || null;
  const currentTeamName = currentTeamIndex >= 0
    ? (currentTeam?.teamName ?? `Team ${currentTeamIndex + 1}`)
    : null;

  return (
    <div className='draft-tv-view'>
      <div className='draft-tv-header'>
        {isCompleted ? (
          <div className='draft-tv-status'>Draft Complete!</div>
        ) : (
          <>
            <div className='draft-tv-meta'>Round {currentRound} of {rounds} · Pick {currentPick} of {teamCount}</div>
            <div className='draft-tv-onclock'>
              On the clock: <strong>{currentTeamName}</strong>
              {currentOccupantName && <span className='draft-tv-occupant'> ({currentOccupantName})</span>}
            </div>
            {draft.commissionerMode && (
              <PickTimer
                timeRemainingSeconds={draft.timeRemainingSeconds}
                isCommissioner={isOwner}
                onSkip={skipExpiredPick}
                onExtend={extendTimer}
              />
            )}
          </>
        )}
        {justPicked && (
          <div className='draft-tv-just-picked' aria-live='polite'>
            Just picked: {justPicked.playerName} ({justPicked.position}, {justPicked.team})
          </div>
        )}
      </div>
      {/* hideViewerBadge: this screen casts to a shared/TV display with no single "current
          viewer" -- whoever happens to be logged into the casting browser's session shouldn't
          have their own identity singled out as "(You)" in front of the whole room. */}
      <DraftBoard hideViewerBadge />
    </div>
  );
}

export default DraftTvView;